import React, { useState, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Button } from 'ui-components/components/button';
import Global from '../../../../store/global-selectors';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { editWebhook, deleteWebhook } from '../../api/integrations-api';
import WebHookForm from './webhook-form';
import { MessageDescriptorPropType } from '../../../../prop-types/intl';

import messages from '../messages';

import style from './style.module.less';
import { emptyArray } from 'ui-components/default-props/empty';

const WebHookList = ({
  webhooks = emptyArray,
  setName,
  name = '',
  setUrl,
  url,
  selectedStore,
  setSelectedStore,
  hookType,
  setHookType,
  webHookDropdown,
  fetchWebhooks,
  setCurrentEditedHookId,
  currentEditedHookId,
  onCancel,
  addWebhookButton,
  validateUrl,
}) => {
  const activeStores = useSelector(Global.selectSellerActiveStores);
  const [currentDeleteHookId, setCurrentDeleteHookId] = useState();

  const onEdit = useCallback(
    (webhook, storeSelected, selectedHookType) => {
      setCurrentEditedHookId(webhook.webhookId);
      setName(webhook.name);
      setUrl(webhook.url);
      setSelectedStore(storeSelected);
      setHookType(selectedHookType);
    },
    [setCurrentEditedHookId, setHookType, setName, setSelectedStore, setUrl],
  );

  const onCheckRemove = (webhookId) => {
    setCurrentDeleteHookId(webhookId);
  };

  const onSaveEdit = useCallback(() => {
    const focusedWebhook = webhooks.find((webhook) => webhook.webhookId === currentEditedHookId);

    const editedObject = {
      ...focusedWebhook,
      name,
      hookType: hookType.value,
      url,
      storeGuid: selectedStore?.storeGuid,
    };

    const editExistingWebhook = async () => {
      try {
        await editWebhook(editedObject);
        fetchWebhooks();
      } catch (error) {
        throw new Error(`Error editing webhook: ${error?.message}`);
      }
    };

    editExistingWebhook();
    setCurrentEditedHookId(null);
  }, [currentEditedHookId, fetchWebhooks, hookType, name, selectedStore, setCurrentEditedHookId, url, webhooks]);

  const onDelete = useCallback(() => {
    const deleteExistingWebhook = async () => {
      try {
        await deleteWebhook(currentDeleteHookId);
        fetchWebhooks();
      } catch (error) {
        throw new Error(`Error deleting webhook: ${error?.message}`);
      }
    };

    deleteExistingWebhook();
    setCurrentEditedHookId(null);
  }, [currentDeleteHookId, fetchWebhooks, setCurrentEditedHookId]);

  const removeWebhookBox = () => {
    return (
      <div className={style['popup-box']}>
        <div>
          <FormattedMessage {...messages.tooltip} />
        </div>
        <Button className={style['remove-button']} onClick={onDelete} theme='danger'>
          <FormattedMessage {...messages.remove} />
        </Button>
      </div>
    );
  };

  return webhooks.map((webhook) => {
    // Check below removes any hooks the backend has indicated as internal API hooks which should not be user facing
    if (webhook.isLabelApiHook) {
      return;
    }

    const storeSelected = activeStores.find((store) => store.storeGuid === webhook.storeGuid);
    const selectedHookType = webHookDropdown.find(({ value }) => value === webhook.hookType);

    if (currentEditedHookId === webhook.webhookId) {
      return (
        <WebHookForm
          key={webhook.webhookId}
          hookType={hookType}
          name={name}
          onCancel={onCancel}
          onSave={onSaveEdit}
          selectedStore={selectedStore}
          setHookType={setHookType}
          setName={setName}
          setSelectedStore={setSelectedStore}
          setUrl={setUrl}
          url={url}
          validateUrl={validateUrl}
          webHookDropdown={webHookDropdown}
        />
      );
    }

    return (
      <div key={webhook.webhookId} className={style['existing-webhooks']}>
        <div className={style['existing-webhooks-line1']}>
          <strong>
            <div>{webhook?.name}</div>
          </strong>
          <div>{storeSelected?.name}</div>
          {selectedHookType && (
            <div>
              <FormattedMessage {...selectedHookType.messageDescriptor} />
            </div>
          )}
          {!currentEditedHookId && !addWebhookButton && (
            <div className={style.buttons}>
              <Button className={style['edit-button']} onClick={() => onEdit(webhook, storeSelected, selectedHookType)} theme='tertiary'>
                <FormattedMessage {...messages.editButton} />
              </Button>
              <DropdownMenu>
                <DropdownMenuToggler
                  className={style['delete-webhook']}
                  hideChevron
                  onClick={() => onCheckRemove(webhook.webhookId)}
                  theme='danger'
                >
                  <FormattedMessage {...messages.removeButton} />
                </DropdownMenuToggler>
                {removeWebhookBox()}
              </DropdownMenu>
            </div>
          )}
        </div>
        <div className={style['existing-webhooks-line2']}>{webhook.url}</div>
        <div className={style['border-line']} />
      </div>
    );
  });
};

WebHookList.propTypes = {
  webhooks: PropTypes.arrayOf(
    PropTypes.shape({
      webhookId: PropTypes.number,
      storeGuid: PropTypes.string,
      name: PropTypes.string,
      url: PropTypes.string,
      hookType: PropTypes.string,
    }),
  ),
  setName: PropTypes.func.isRequired,
  name: PropTypes.string,
  setUrl: PropTypes.func.isRequired,
  url: PropTypes.string,
  selectedStore: PropTypes.object,
  setSelectedStore: PropTypes.func.isRequired,
  hookType: PropTypes.object.isRequired,
  setHookType: PropTypes.func.isRequired,
  webHookDropdown: PropTypes.arrayOf(
    PropTypes.shape({
      messageDescriptor: MessageDescriptorPropType,
      value: PropTypes.string,
    }),
  ).isRequired,
  fetchWebhooks: PropTypes.func.isRequired,
  setCurrentEditedHookId: PropTypes.func.isRequired,
  currentEditedHookId: PropTypes.number,
  onCancel: PropTypes.func.isRequired,
  addWebhookButton: PropTypes.bool.isRequired,
  validateUrl: PropTypes.func.isRequired,
};

export default WebHookList;
